import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GetAllDevicesParams, VelavuAPI, VelavuDevice } from "velavu-js-api";
import { useGlobalEmitter } from "../emitter/emitter-context";
import { useSubscribeCombinedDeviceUpdates } from "../emitter/stream-helper";

interface InternalListDevicesState {
	devices: VelavuDevice[];
	isLoading: boolean;
	error: unknown | null;
	continuationToken: string | null;
}

export interface ListDevicesState {
	devices: VelavuDevice[];
	isLoading: boolean;
	error: unknown | null;
	hasMore: boolean;
}

function internalListDevicesStateToSuccessState(
	state: InternalListDevicesState,
): ListDevicesState {
	return {
		devices: state.devices,
		isLoading: state.isLoading,
		error: state.error,
		hasMore: state.continuationToken !== null,
	};
}

export interface ListDevicesActions {
	loadDevices: (params?: GetAllDevicesParams) => Promise<void>;
	loadMore: () => Promise<void>;
	registerDevice: (device: VelavuDevice) => void;
	unregisterDevice: (deviceID: string) => void;
}

export function useListDevicesState(): [ListDevicesState, ListDevicesActions] {
	const emitter = useGlobalEmitter();

	const [devicesState, setDevicesState] = useState<InternalListDevicesState>({
		devices: [],
		isLoading: false,
		error: null,
		continuationToken: null,
	});

	const activeRequestID = useRef(0);
	const initialRequestParams = useRef<GetAllDevicesParams | undefined | null>(
		null,
	);

	const loadDevices = useCallback(
		async (params?: GetAllDevicesParams) => {
			//Get a request ID
			activeRequestID.current += 1;
			const requestID = activeRequestID.current;

			//Reset the devices state
			setDevicesState((state) => ({
				...state,
				isLoading: true,
			}));

			//Store the params
			initialRequestParams.current = params;

			//Load the devices
			try {
				const allDevices =
					await VelavuAPI.devices.getAllDevices(params);

				//Make sure our request is still active
				if (activeRequestID.current === requestID) {
					setDevicesState({
						devices: allDevices.data,
						isLoading: false,
						error: null,
						continuationToken: allDevices.continuationToken ?? null,
					});
				}
			} catch (error) {
				//Make sure our request is still active
				if (activeRequestID.current === requestID) {
					setDevicesState({
						devices: [],
						isLoading: false,
						error,
						continuationToken: null,
					});
				}

				//Rethrow the error
				throw error;
			}
		},
		[setDevicesState],
	);

	const loadMore = useCallback(async () => {
		//Check state
		if (devicesState.isLoading) {
			console.warn(
				"Attempted to load more devices while already loading",
			);
			return;
		}
		if (devicesState.continuationToken === null) {
			console.warn(
				"Attempted to load more devices without a continuation token",
			);
			return;
		}
		if (initialRequestParams.current === null) {
			console.warn(
				"Attempted to load more devices without initial request params",
			);
			return;
		}

		//Reset the devices state
		const newDevicesState: Readonly<InternalListDevicesState> = {
			...devicesState,
			isLoading: true,
			error: null,
		};
		setDevicesState(newDevicesState);

		//Load more devices
		try {
			const moreDevices = await VelavuAPI.devices.getAllDevices(
				initialRequestParams.current,
				{ continuationToken: devicesState.continuationToken },
			);

			setDevicesState({
				...newDevicesState,
				isLoading: false,
				devices: [...newDevicesState.devices, ...moreDevices.data],
				continuationToken: moreDevices.continuationToken ?? null,
			});
		} catch (error) {
			setDevicesState({
				...newDevicesState,
				isLoading: false,
				error: error,
			});

			//Rethrow the error
			throw error;
		}
	}, [devicesState, setDevicesState]);

	const registerDevice = useCallback(
		(newDevice: VelavuDevice) => {
			setDevicesState((devicesState) => {
				//Copy the current state
				const updatedDevicesState = { ...devicesState };
				const updatedDevices = [...updatedDevicesState.devices];

				//Check if the device is already in the list
				const deviceIndex = updatedDevices.findIndex(
					(device) => device.id === newDevice.id,
				);
				if (deviceIndex !== -1) {
					//Update the device
					updatedDevices[deviceIndex] = newDevice;
				} else {
					//Add the new device
					updatedDevices.push(newDevice);
				}

				//Return the updated state
				updatedDevicesState.devices = updatedDevices;
				return updatedDevicesState;
			});

			emitter.emit("device", {
				id: newDevice.id,
				data: newDevice,
			});
		},
		[emitter, setDevicesState],
	);

	const unregisterDevice = useCallback(
		async (deviceID: string) => {
			try {
				await VelavuAPI.devices.deleteSpecificDevice(deviceID);
				emitter.emit("device", {
					id: deviceID,
					data: null,
				});
				setDevicesState((devicesState) => {
					//Copy the current state
					const updatedDevicesState = { ...devicesState };
					const updatedDevices = [...updatedDevicesState.devices];

					//Remove the device with the matching ID
					const deviceIndex = updatedDevices.findIndex(
						(device) => device.id === deviceID,
					);
					if (deviceIndex !== -1) {
						updatedDevices.splice(deviceIndex, 1);
					}

					//Return the updated state
					updatedDevicesState.devices = updatedDevices;
					return updatedDevicesState;
				});
			} catch (error) {
				console.log(error);
			}
		},
		[emitter, setDevicesState],
	);

	//Subscribe to remote and locally emitted device updates
	useSubscribeCombinedDeviceUpdates(
		useCallback(
			(update) => {
				setDevicesState((devicesState) => {
					//Try to find the device in the list
					const deviceIndex = devicesState.devices.findIndex(
						(device) => device.id === update.id,
					);
					if (deviceIndex === -1) {
						return devicesState;
					}

					//Apply the update
					const updatedDevices = [...devicesState.devices];
					if (update.data === null) {
						//Remove the deleted device
						updatedDevices.splice(deviceIndex, 1);
					} else {
						//Apply the update
						updatedDevices[deviceIndex] = update.data;
					}
					return {
						...devicesState,
						devices: updatedDevices,
					};
				});
			},
			[setDevicesState],
		),
	);

	useEffect(() => {
		//Load the initial devices
		loadDevices();
	}, []); //eslint-disable-line react-hooks/exhaustive-deps

	//Convert the internal state to public state
	const publicDevicesState = useMemo(() => {
		return internalListDevicesStateToSuccessState(devicesState);
	}, [devicesState]);

	//Memoize the actions
	const actions: ListDevicesActions = useMemo(() => {
		return {
			loadDevices,
			loadMore,
			unregisterDevice,
			registerDevice,
		};
	}, [loadDevices, loadMore, registerDevice, unregisterDevice]);

	return [publicDevicesState, actions];
}
